// ── Micro encuestas: NPS del Home y "¿para qué ramo?" del 3er PAE ────────────
// Ítems 9 y 10 del BACKLOG (OK del fundador 2026-08-28). Dominio PURO, sin
// React: la cuota vive en localStorage bajo UNA clave (`mipae:encuestas`) y
// toda lectura/escritura va envuelta en try/catch con fail-safe hacia NO
// molestar (sin storage no se muestra nada), mismo contrato que queCambio.js.
//
// Reglas (todas con reloj inyectable `ahora` para el test):
// - NPS: máximo 1 vez al MES por usuario (la marca es por userId, así un
//   dispositivo compartido no cruza cuentas).
// - Ramo del PAE: se pregunta UNA vez, al guardar el TERCER PAE. El contador
//   cuenta SOLO guardados nuevos (jamás reaperturas desde "Mis PAE": esa señal
//   la separa el callback `onPAESaved(esNuevoGuardado)`). Si al tercero la
//   cuota semanal estaba ocupada, queda pendiente y se ofrece al siguiente
//   guardado: la pregunta no se pierde, pero tampoco se apura.
// - JAMÁS dos micro encuestas seguidas: cuota compartida por dispositivo de
//   UNA por semana entre ambas.
// - Descartar con la X consume la cuota IGUAL que responder (anti-culpa: no
//   se insiste a quien dijo que no).
// - A telemetría viaja SOLO catálogo cerrado y números: el puntaje 0-10, el
//   rol del perfil y el id del ramo. Jamás texto libre.

import { hayActividadDeTurno } from './queCambio';

export const CLAVE_ENCUESTAS = 'mipae:encuestas';

const MS_DIA = 24 * 60 * 60 * 1000;
export const MS_SEMANA = 7 * MS_DIA;
// "Un mes" operativo: 30 días. No se usa el calendario porque la regla es de
// descanso entre preguntas, no de facturación.
export const MS_MES = 30 * MS_DIA;
export const PAES_PARA_ENCUESTA = 3;
export const MOMENTO_TERCER_PAE = 'tercer_pae';

// Escala NPS estándar: 0 a 10, un toque.
export const PUNTAJES_NPS = Object.freeze([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);

// Catálogo cerrado de ramos (decidido por el fundador en el ítem 10). El id es
// lo que viaja a eventos_v2; el label es lo que ve la persona.
export const RAMOS_PAE = Object.freeze([
  { id: 'fundamentos',       label: 'Fundamentos' },
  { id: 'medico_quirurgico', label: 'Médico quirúrgico' },
  { id: 'pediatria',         label: 'Pediatría' },
  { id: 'salud_mental',      label: 'Salud mental' },
  { id: 'gestion',           label: 'Gestión' },
  { id: 'internado',         label: 'Internado' },
  { id: 'otro',              label: 'Otro' },
]);

export function esRamoValido(id) {
  return RAMOS_PAE.some((r) => r.id === id);
}

export function esPuntajeNpsValido(p) {
  return Number.isInteger(p) && p >= 0 && p <= 10;
}

// Kill-switches ON por defecto (regla del repo: `!== 'false'`, nunca `=== 'true'`).
// Son funciones y no constantes para que el test pueda alternarlas.
export function npsHabilitado() {
  return process.env.REACT_APP_NPS_ENABLED !== 'false';
}
export function encuestaMomentoHabilitada() {
  return process.env.REACT_APP_ENCUESTA_MOMENTO_ENABLED !== 'false';
}

// ── Estado en localStorage ───────────────────────────────────────────────────
// { v: 1, ultimaMicro: ts|null, nps: { [userId]: ts }, paes: { [userId]: { guardados, encuestaRamo } } }
// `encuestaRamo`: 'pendiente' (aún no se preguntó) | 'consumida' (respondida o
// descartada: no se vuelve a preguntar).
const ESTADO_VACIO = () => ({ v: 1, ultimaMicro: null, nps: {}, paes: {} });

function sanear(raw) {
  const e = ESTADO_VACIO();
  if (!raw || typeof raw !== 'object') return e;
  if (typeof raw.ultimaMicro === 'number' && Number.isFinite(raw.ultimaMicro)) e.ultimaMicro = raw.ultimaMicro;
  if (raw.nps && typeof raw.nps === 'object') {
    for (const [k, v] of Object.entries(raw.nps)) {
      if (typeof v === 'number' && Number.isFinite(v)) e.nps[k] = v;
    }
  }
  if (raw.paes && typeof raw.paes === 'object') {
    for (const [k, v] of Object.entries(raw.paes)) {
      if (!v || typeof v !== 'object') continue;
      const guardados = Number.isInteger(v.guardados) && v.guardados >= 0 ? v.guardados : 0;
      const encuestaRamo = v.encuestaRamo === 'consumida' ? 'consumida' : 'pendiente';
      e.paes[k] = { guardados, encuestaRamo };
    }
  }
  return e;
}

// Devuelve el estado saneado, o null si el storage no está disponible (la
// señal de "no molestar": sin persistencia no se puede prometer la cuota).
export function leerEstadoEncuestas() {
  try {
    const raw = localStorage.getItem(CLAVE_ENCUESTAS);
    if (raw == null) return ESTADO_VACIO();
    return sanear(JSON.parse(raw));
  } catch {
    return null;
  }
}

function escribirEstado(estado) {
  try {
    localStorage.setItem(CLAVE_ENCUESTAS, JSON.stringify(estado));
    return true;
  } catch {
    return false;
  }
}

// La cuota COMPARTIDA: ¿hubo alguna micro encuesta (mostrada, respondida o
// descartada) en la última semana en este dispositivo?
export function cuotaSemanalLibre(estado, ahora = Date.now()) {
  if (!estado) return false;
  if (estado.ultimaMicro == null) return true;
  return ahora - estado.ultimaMicro >= MS_SEMANA;
}

// ── NPS ──────────────────────────────────────────────────────────────────────
// `registros` (opcional): los registros de turno locales, para no preguntar a
// mitad de turno. Misma regla que "Qué cambió" (hueco 18): una card en el Home
// jamás interrumpe a quien está registrando una cama.
// `perfilCreadoEn` (ISO o ms): el NPS no se le pregunta a quien recién
// terminó el onboarding (auditoría del 2026-09-01: aterrizaba en su primer
// Home con "¿recomendarías MiPAE?" sin haber usado nada, y el dato salía
// contaminado además de competir con el primer minuto). Se exige una semana
// de antigüedad; sin fecha conocida, no se pregunta (fail-safe hacia no
// molestar, misma doctrina de "Qué cambió").
export const MS_ANTIGUEDAD_NPS = 7 * 24 * 60 * 60 * 1000;

export function puedeMostrarNps({ userId, ahora = Date.now(), registros = [], perfilCreadoEn = null } = {}) {
  if (!userId) return false;
  const creado = perfilCreadoEn == null ? NaN : new Date(perfilCreadoEn).getTime();
  if (!Number.isFinite(creado) || ahora - creado < MS_ANTIGUEDAD_NPS) return false;
  const estado = leerEstadoEncuestas();
  if (!estado) return false;
  if (!cuotaSemanalLibre(estado, ahora)) return false;
  if (hayActividadDeTurno(registros, ahora)) return false;
  const ultimo = estado.nps[userId];
  if (ultimo == null) return true;
  return ahora - ultimo >= MS_MES;
}

// Responder o descartar: las dos consumen la cuota mensual del usuario Y la
// semanal del dispositivo. No se distingue a propósito (anti-culpa).
export function consumirNps({ userId, ahora = Date.now() } = {}) {
  if (!userId) return false;
  const estado = leerEstadoEncuestas();
  if (!estado) return false;
  estado.nps[userId] = ahora;
  estado.ultimaMicro = ahora;
  return escribirEstado(estado);
}

// ── Encuesta del 3er PAE ─────────────────────────────────────────────────────
function paesDe(estado, userId) {
  if (!estado.paes[userId]) estado.paes[userId] = { guardados: 0, encuestaRamo: 'pendiente' };
  return estado.paes[userId];
}

function ofrecerRamo(estado, userId, ahora) {
  const p = estado.paes[userId];
  if (!p || p.encuestaRamo !== 'pendiente') return false;
  if (p.guardados < PAES_PARA_ENCUESTA) return false;
  return cuotaSemanalLibre(estado, ahora);
}

// Se llama UNA vez por guardado NUEVO (jamás al reabrir). Devuelve el conteo
// y si toca ofrecer la pregunta ahora mismo. Si el storage falla, no se
// cuenta ni se ofrece: mejor perder una respuesta que preguntar dos veces.
export function registrarPaeGuardado({ userId, ahora = Date.now() } = {}) {
  if (!userId) return { guardados: 0, ofrecerEncuesta: false };
  const estado = leerEstadoEncuestas();
  if (!estado) return { guardados: 0, ofrecerEncuesta: false };
  const p = paesDe(estado, userId);
  p.guardados += 1;
  if (!escribirEstado(estado)) return { guardados: p.guardados, ofrecerEncuesta: false };
  return { guardados: p.guardados, ofrecerEncuesta: ofrecerRamo(estado, userId, ahora) };
}

// Lectura sin efectos: ¿está pendiente y con cuota libre?
export function puedeMostrarEncuestaRamo({ userId, ahora = Date.now() } = {}) {
  if (!userId) return false;
  const estado = leerEstadoEncuestas();
  if (!estado) return false;
  return ofrecerRamo(estado, userId, ahora);
}

// Responder o descartar: la pregunta se hace UNA vez en la vida del usuario
// en este dispositivo, y consume la cuota semanal compartida.
export function consumirEncuestaRamo({ userId, ahora = Date.now() } = {}) {
  if (!userId) return false;
  const estado = leerEstadoEncuestas();
  if (!estado) return false;
  const p = paesDe(estado, userId);
  p.encuestaRamo = 'consumida';
  estado.ultimaMicro = ahora;
  return escribirEstado(estado);
}
